/**
 * The category hub's data (web plan W3.1): the backend's `categoryPage`
 * (B3.7b) for a route key `l1` or `l1/l2` (@/seo/categoryHubs
 * `categoryHubKey`), in the route's locale. The SEO server sends the same
 * document with the same variables (server/routes.mjs), so the head it builds
 * is the one this page shows.
 *
 * Status like a detail page (@/utils/detailStatus, web plan W0.2): a key no
 * category can have, a NOT_FOUND answer (unknown category, `l2` not a child
 * of `l1`) or a null result is `not_found`; 429 / 5xx / network is `error`
 * (robots untouched).
 */
import { CATEGORY_PAGE } from '@/graphql/queries';
import { usePreloadedQuery } from '@/hooks/routePreload';
import { categoryPageVariables, type CategoryHubPage } from '@/seo/categoryHubs';
import type { Locale } from '@/seo/locales';
import { detailStatus, type DetailStatus } from '@/utils/detailStatus';
import { queryErrorMessage } from './useAppData';

export interface CategoryPageState {
  hub: CategoryHubPage | null;
  status: DetailStatus;
  error: string | null;
  refetch: () => void;
}

export function useCategoryPage(key: string | null, locale: Locale): CategoryPageState {
  // The route module's server copy answers first (web plan W4.5).
  const { data, loading, error, refetch } = usePreloadedQuery<{ categoryPage?: CategoryHubPage | null }>(CATEGORY_PAGE, {
    skip: !key,
    variables: key ? categoryPageVariables(key, locale) : undefined,
    // Retry goes back through `loading` instead of leaving the error up.
    notifyOnNetworkStatusChange: true,
  });
  const hub = data?.categoryPage ?? null;
  return {
    hub,
    status: detailStatus({ item: hub, loading, error, validParam: Boolean(key) }),
    error: queryErrorMessage(error),
    refetch: () => {
      void refetch();
    },
  };
}
